import React from 'react';
import { Tooltip } from "@mui/material";

import Grid from '@mui/material/Grid2';

import { 
  CharacterSpell,
  CharacterAbility,
  Spell,
  SpellsFromList,
  Ability,
  CharacterFeatureSource,
  CharacterFeature,
  Character,
  StoredSpell,
  SpellsFromListWithCosts,
  CharacterSheetInterface,
} from "@dndModels";

import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import DandDAPI from '@utilities/smart_api';
import CommonUtilities from "@utilities/common_utilities";

import ImageFromString from "@dndComponents/display/ImageFromString";
import { CASTING_TIME_MAP } from "@dndModels/Constants";
import { Visibility } from '@mui/icons-material';

interface Props {
  char?: CharacterSheetInterface;
  cf?: CharacterFeature | null;
  cfs?: CharacterFeatureSource | null;
  spell: CharacterSpell | StoredSpell | Spell | Ability | CharacterAbility;
  show_level?: boolean;
  show_ritual?: boolean;
  fontSize?: number;
  hide_details?: boolean;
  fontWeight?: "-moz-initial" | "inherit" | "initial" | "revert" | "unset" | "normal" | (number & {}) | "bold" | "bolder" | "lighter" | undefined;
}

const char_util = DandDCharacterUtilities.getInstance();
const api = DandDAPI.getInstance();
const common_util = CommonUtilities.getInstance();

export default function ViewSpell({
  char,
  cf = null,
  cfs = null,
  spell,
  show_level = false,
  show_ritual = false,
  hide_details = false,
  fontSize = 11,
  fontWeight = undefined,
}: Props) {

  const get_level_string = (level: number) => {
    if (level === 0) {
      return "Cantrip";
    } else if (level === 1) {
      return "1st";
    } else if (level === 2) {
      return "2nd";
    } else if (level === 3) {
      return "3rd";
    } else {
      return `${level}th`;
    }
  }
  
  // if (spell.name.includes("Vicious")) {
  //   debugger;
  // }
  let detail = "";
  let concentration = false;
  let ritual = false;
  let level = 0;
  let name = spell.true_name;
  // if (spell.name.includes("Cast Spell")) {
  //   debugger;
  // }
  if ((spell instanceof CharacterAbility || spell instanceof CharacterSpell) && 
    spell._name !== "") {
    name = spell._name;
  } else if (cfs && 
    !(spell instanceof CharacterAbility && 
      (spell.the_ability instanceof SpellsFromList || 
        spell.the_ability instanceof SpellsFromListWithCosts))) {
    name = char_util.getStringWithReplacements(name, cfs);
  } else if (spell instanceof CharacterAbility && 
    (spell.the_ability instanceof SpellsFromList || 
      spell.the_ability instanceof SpellsFromListWithCosts) && 
    spell.the_spell) {
    name = spell.the_spell.name;
  }
  if (spell instanceof Spell) {
    concentration = spell.concentration;
    ritual = spell.ritual;
    if (spell.level) {
      level = spell.level;
    }
  } else if (spell instanceof CharacterSpell) {
    if (!char) {
      detail = spell.source_name;
    } else if (spell.cf_id === "") {
      const cfs2 = char.char_feature_sources.find(o => o.source_id === spell.source_id);
      if (cfs2) {
        detail = char_util.getUseName(cfs2);
      } else {
        detail = spell.source_name;
      }
    } else {
      const cf2 = char.mind.spellcasting_features.find(o => o.true_id === spell.cf_id);
      if (cf2) {
        const cfs2 = char.char_feature_sources.find(o => o.true_id === cf2.source_true_id);
        if (cfs2) {
          detail = char_util.getUseName(cfs2);
        } else {
          detail = spell.source_name;
        }
      } else {
        detail = spell.source_name;
      }
    }
    if (spell.spell) {
      concentration = spell.spell.concentration;
      ritual = spell.ritual;
      if (spell.spell.level) {
        level = spell.spell.level;
      }
    }
  } else if (spell instanceof StoredSpell) {
    concentration = spell.concentration;
    if (spell.level) {
      level = spell.level;
    }
  } else if (spell instanceof CharacterAbility) {
    if (cfs) {
      detail = char_util.getUseName(cfs);
    } else if (char) {
      const cfs2 = char_util.getCFSForCF(char, spell);
      if (cfs2) {
        detail = char_util.getUseName(cfs2);
      } else {
        detail = spell.source_name;
      }
    } else {
      detail = spell.source_name;
    }
    const the_ability = spell.the_ability;
    if (the_ability === null || 
      the_ability instanceof Ability ||
      the_ability instanceof SpellsFromList ||
      the_ability instanceof SpellsFromListWithCosts
    ) {
      concentration = spell.concentration;
    }
    level = spell.level;
  } else if (spell instanceof Ability) {
    // if (spell.spell) {
    //   concentration = spell.spell.concentration;
    //   if (spell.spell.level) {
    //     level = spell.spell.level;
    //   }
    // }
  }
  const damage_types = common_util.unique_array(spell.damage_types);
  const effect_types = common_util.unique_array(spell.effect_types).filter(o => o !== "Damage");
  const detail_format = "{obj.level_string} - {obj.school}";
  const spell_detail = hide_details ? "" : char_util.getStringWithReplacementsWithObject({ clean_me: detail_format, backup: "", obj: spell });
  const use_detail = (detail.length > 0 ? detail + " " + spell_detail : spell_detail);
  const times = spell.casting_times.map(o => CASTING_TIME_MAP[o]).join(", ") + (spell.duration === "Instantaneous" ? "" : ", Duration: " + spell.duration);
  return (
    <Grid container spacing={0} direction="row">
      <Grid
        style={{  
          fontSize: `${fontSize}px`, 
          fontWeight: fontWeight,
          marginLeft: "4px" 
        }}
        size={{
          xs: 12
        }}>
        { name } 
        &nbsp;
        { concentration && 
          <Tooltip title="This Spell takes your Concentration">
            <span style={{ backgroundColor: "black", color: "white", fontSize: "7px" }}>&nbsp;C&nbsp;</span>
          </Tooltip> 
        }
        { show_ritual && ritual && 
          <Tooltip title="This Spell can be cast as a Ritual">
            <span style={{ backgroundColor: "black", color: "white", fontSize: "7px" }}>&nbsp;R&nbsp;</span> 
          </Tooltip>
        }
        &nbsp;
        { spell.components.includes("V") && 
          <Tooltip title="This Spell has a Verbal component (meaning you have to say some kind of incantation to cast it).  It cannot be cast if you are Silenced, and the Verbal component makes it so creatures that can hear you can recognize that you're casting a spell, which may allow them to counter it.">
            <span style={{ backgroundColor: "black", color: "white", fontSize: "7px" }}>&nbsp;V&nbsp;</span> 
          </Tooltip>
        }
        { spell.components.includes("S") && 
          <Tooltip title="This Spell has a Somatic component (meaning you have to make some kind of gesture to cast it).  It cannot be cast if you are Grappled, and the Somatic component makes it so creatures that can see you can recognize that you're casting a spell, which may allow them to counter it.">
            <span style={{ backgroundColor: "black", color: "white", fontSize: "7px" }}>&nbsp;S&nbsp;</span> 
          </Tooltip>
        }
        { spell.components.includes("M") && 
          <Tooltip title={`This Spell has a Material component.  This means you have to have the listed item in order to cast the spell.  If the material component doesn't have a cost associated with it, then a Focus can be used instead, or if you have a spell component pouch then you can assume that you have it.  If it does have a cost associated, then you must have the item on you to be able to cast the spell.  Some spells consume the material component as well.  Material Component: ${spell.material_component}`}>
            <span style={{ backgroundColor: "black", color: "white", fontSize: "7px" }}>&nbsp;M&nbsp;</span> 
          </Tooltip>
        }
        &nbsp;
        { spell.sight && 
          <Tooltip title={`This Spell requires the ability to see the target.`}>
            <span style={{ backgroundColor: "black", color: "white", fontSize: "7px" }}> 
              &nbsp;<Visibility style={{ fontSize: "7px" }} />&nbsp;
            </span> 
          </Tooltip>
        }
        &nbsp;
        { show_level && <span>({ get_level_string(level) })</span> }
      </Grid>
      { (damage_types.length > 0 || effect_types.length > 0) &&
        <Grid
          style={{ marginLeft: "4px", marginTop: "4px" }}
          className="small_label gray"
          size={{
            xs: 12
          }}>
          { damage_types.map((dt, key) => {
            return (
              <span key={key}>
                { key > 0 && ", "}
                { dt === "Typeless" ? "Typeless" : 
                  <ImageFromString image={dt} />
                }
              </span>
            );
          })}
          { damage_types.length > 0 && effect_types.length > 0 && ", "}
          { effect_types.join(", ") }
        </Grid>
      }
      { use_detail.length > 0 &&
        <Grid
          style={{ marginLeft: "4px", marginTop: "-4px" }}
          size={{
            xs: 12
          }}>
          <span className="small_label gray">{ use_detail }</span>
        </Grid>
      }
      <Grid
        style={{ marginLeft: "4px", marginTop: "-4px" }}
        size={{
          xs: 12
        }}>
        <span className="small_label gray">{ times }</span>
      </Grid>
    </Grid>
  );
}
